import { useEffect, useState } from 'react'

/**
 * MotivationalBanner
 * ──────────────────────────────────────────────────────────────
 * Auto-dismissing banner that surfaces a short companion-voice
 * aphorism. Companion-identity gamification PR (2026-05-03).
 *
 * Used on:
 *   - PlacementPage subject_intro and subject_transition screens
 *   - QuestionPage practice entry
 *   - Exam / Mini-Exam entry hook
 *
 * Behaviour:
 *   - Slides in from the top on mount.
 *   - Stays for `durationMs` (default 4500ms - long enough to read
 *     a 1-2-clause sentence twice, short enough not to overstay).
 *   - Fades out + unmounts.
 *   - Tap-to-dismiss anywhere on the banner.
 *   - Non-blocking: the underlying primary action (Vamos lá / answer
 *     the question / start exam) remains interactive throughout.
 *
 * Caller controls WHICH saying via the `message` prop - use
 * pickMotivationalSaying(subjectName) from lib/motivationalSayings
 * to get one. Pick once at parent mount and pass as a stable prop
 * so re-renders don't reroll the saying mid-display.
 */

interface MotivationalBannerProps {
  message: string
  /** Total visible duration in ms. Default 4500. */
  durationMs?: number
}

export function MotivationalBanner({ message, durationMs = 4500 }: MotivationalBannerProps) {
  // 'enter' = sliding in (initial 200ms), 'visible' = full opacity,
  // 'exit' = fading out (last 400ms before unmount). Three-stage
  // animation lets the slide-in + fade-out feel gentle.
  const [stage, setStage] = useState<'enter' | 'visible' | 'exit'>('enter')

  useEffect(() => {
    const enterT = window.setTimeout(() => setStage('visible'), 50)
    const exitT  = window.setTimeout(() => setStage('exit'), Math.max(0, durationMs - 400))
    const dismissT = window.setTimeout(() => setStage('exit'), durationMs)
    return () => {
      window.clearTimeout(enterT)
      window.clearTimeout(exitT)
      window.clearTimeout(dismissT)
    }
  }, [durationMs])

  // After 'exit' stage starts, give the CSS transition 400ms then unmount.
  const [unmounted, setUnmounted] = useState(false)
  useEffect(() => {
    if (stage !== 'exit') return
    const t = window.setTimeout(() => setUnmounted(true), 450)
    return () => window.clearTimeout(t)
  }, [stage])

  if (unmounted) return null

  const opacity = stage === 'enter' ? 0 : stage === 'visible' ? 1 : 0
  const translateY = stage === 'enter' ? '-12px' : '0px'

  return (
    <div
      className="fixed top-3 left-3 right-3 z-40 max-w-md mx-auto pointer-events-none"
      style={{
        opacity,
        transform: `translateY(${translateY})`,
        transition: 'opacity 350ms ease, transform 350ms ease',
      }}
      role="status"
      aria-live="polite"
    >
      <button
        onClick={() => setStage('exit')}
        className="w-full pointer-events-auto bg-indigo-600 text-white shadow-lg rounded-xl px-4 py-3 text-left flex items-start gap-2.5"
      >
        <span className="text-base shrink-0 leading-none mt-0.5" aria-hidden>💡</span>
        <span className="text-sm leading-snug flex-1">{message}</span>
      </button>
    </div>
  )
}
